import React, { useState, useEffect, useRef } from 'react';
import CommonModal from './CommonModal';
import '../../styles/staffPin.css';

const MAX_PIN_LENGTH = 6;

/**
 * Collects a PIN and hands it to the caller - it deliberately does NOT validate it.
 *
 * Used where the SERVER is the authority on the PIN (Add Promotion: create-offer accepts either a
 * manager's staff PIN or the restaurant's super admin PIN). The existing PIN prompts cannot be
 * used for that:
 *  - useRequirePermission / StaffPinModal pre-check the PIN against /v3/staff-auth/validate-pin,
 *    which only knows employee PINs, so it would wrongly reject a super admin PIN;
 *  - ValidatePinModal and StaffPinModal are four-digit-only, but a super admin PIN is typed into
 *    superadmin-ci2 as free text of up to six characters, and some are not four digits.
 *
 * So: one password field (up to six characters, any keyboard), a Continue button, and an inline
 * error line for the server's refusal. The PIN lives only in this component's state while the
 * prompt is open and is cleared whenever it opens, closes or is submitted.
 *
 * Reuses the staff PIN sheet styling (staffPin.css is scoped to the #staffPinModal element id).
 */
const AuthorizationPinModal = ({
  isOpen,
  onCancel,
  onConfirm,
  title = 'Authorization Required',
  subtitle = 'Enter a manager PIN or the restaurant super admin PIN to continue',
  error = '',
}) => {
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    setValue('');
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const submit = () => {
    const pin = value.trim();
    if (!pin) {
      return;
    }
    setValue('');
    onConfirm(pin);
  };

  return (
    <CommonModal isOpen={isOpen} onClose={onCancel} elId="staffPinModal">
      <div className="pinModalBody">
        <div className="pinModalIcon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 10-8 0v2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <button type="button" className="pinModalClose" onClick={onCancel} aria-label="Close">
          &times;
        </button>

        <h1 className="pinModalTitle">{title}</h1>
        <p className="pinModalSubtitle">{subtitle}</p>

        <div className="pinDigitsRow" style={{ width: '100%' }}>
          <input
            ref={inputRef}
            type="password"
            autoComplete="off"
            maxLength={MAX_PIN_LENGTH}
            className="pinDigitBox"
            style={{ width: '100%', letterSpacing: '0.4em' }}
            aria-label="PIN"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                submit();
              }
            }}
          />
        </div>

        <p className="pinModalError" role="alert">{error || ' '}</p>

        <button className="pinValidateBtn" onClick={submit} disabled={!value.trim()}>
          Continue
        </button>

        <button className="pinBackLink" onClick={onCancel}>Close</button>
      </div>
    </CommonModal>
  );
};

export default AuthorizationPinModal;
